/**
 * Timeline Markers Component
 * Handles all timeline marker rendering (scale lines, activity markers, logos)
 */

import React from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import DualCompanyLogo from '@/components/ui/dual-company-logo';
import { getCompanyDisplayInfo } from '@/lib/companyDisplayUtils';
import { createCompanySlug } from '@/lib/companyUtils';
import { companies, activities } from '../../../../db';
import { TimelineMarker } from './types';

interface TimelineMarkersProps {
  timelineMarkers: TimelineMarker[];
  activityMarkers: Array<{
    position: number;
    activityId: number;
    companyId: number;
    companyName: string;
    companyLogo: string;
    startDate: Date;
    title: string;
  }>;
  containerWidth: number;
  zoomLevel: number;
  projectLanes: number[];
  TIMELINE_HEIGHT: number;
  TIMELINE_PADDING: number;
}

export const TimelineMarkers: React.FC<TimelineMarkersProps> = ({
  timelineMarkers,
  activityMarkers,
  containerWidth,
  zoomLevel,
  projectLanes,
  TIMELINE_HEIGHT,
  TIMELINE_PADDING
}) => {
  const timelineWidth = Math.max(Math.max(containerWidth, 1200) * zoomLevel, Math.max(containerWidth, 1200));
  const timelineHeight = (Math.max(...projectLanes, 0) + 1) * TIMELINE_HEIGHT + TIMELINE_PADDING + 20;

  return (
    <>
      {/* Timeline background */}
      <div 
        className="absolute top-0 left-0 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 rounded-lg"
        style={{ 
          width: `${timelineWidth}px`,
          height: '100%',
          zIndex: 1
        }}
      />
      
      {/* Vertical marker lines - SEPARATE CONTAINER with lower z-index */}
      <div 
        className="absolute top-0 left-0"
        style={{ 
          width: `${timelineWidth}px`,
          height: '100%',
          zIndex: 5
        }}
      >
        {/* Scale marker lines */}
        {timelineMarkers.map((marker, index) => (
          <div
            key={`scale-line-${index}`}
            className={`absolute ${
              marker.type === 'major' ? 'bg-border/30' : 'bg-border/10'
            } w-px`}
            style={{ 
              left: `${marker.position + 40}px`,
              top: '64px',
              height: `calc(100% - 64px)`
            }}
          />
        ))}
        
        {/* Activity start marker lines */}
        {activityMarkers.map((activityMarker) => (
          <div
            key={`activity-line-${activityMarker.activityId}`}
            className="absolute w-0.5 bg-primary/50"
            style={{ 
              left: `${activityMarker.position + 40 - 1}px`,
              top: '64px',
              height: `calc(100% - 64px)`
            }}
          />
        ))}
      </div>
      
      {/* Timeline header markers */}
      <div 
        className="absolute top-0 left-0 h-16 border-b border-border/20"
        style={{ 
          width: `${timelineWidth}px`,
          zIndex: 15
        }}
      >
        {timelineMarkers.map((marker, index) => (
          <div
            key={`header-marker-${index}`}
            className="absolute"
            style={{ left: `${marker.position + 40}px` }}
          >
            <div className="flex flex-col items-center">
              <div 
                className={`w-px h-3 ${marker.type === 'major' ? 'bg-border' : 'bg-border/50'}`}
              />
              <span 
                className={`text-xs mt-1 ${
                  marker.type === 'major' 
                    ? 'text-foreground font-medium' 
                    : 'text-muted-foreground'
                }`}
              >
                {marker.label}
              </span>
            </div>
          </div>
        ))}
      </div>
      
      {/* Activity company logos - SEPARATE CONTAINER outside header */}
      <div 
        className="absolute top-0 left-0"
        style={{ 
          width: `${timelineWidth}px`,
          height: '100%',
          zIndex: 60,
          pointerEvents: 'none'
        }}
      >
        {activityMarkers.map((activityMarker) => {
          const activity = activities.find(a => a.id === activityMarker.activityId);
          const displayInfo = activity ? getCompanyDisplayInfo(activity, companies) : null;
          
          // Calculate container size based on logo type
          const isDual = displayInfo?.isDualCompany;
          const containerWidth = isDual ? 48 : 24; // w-12 for dual, w-6 for single
          const containerHeight = isDual ? 32 : 24; // h-8 for dual, h-6 for single
          const leftOffset = isDual ? 24 : 12; // Center the container properly
          
          return (
            <div
              key={`activity-logo-${activityMarker.activityId}`}
              className="absolute flex items-center justify-center"
              style={{ 
                left: `${activityMarker.position + 40 - leftOffset}px`,
                top: '44px',
                width: `${containerWidth}px`,
                height: `${containerHeight}px`,
                zIndex: 70,
                pointerEvents: 'auto'
              }}
              title={`${activityMarker.companyName} - ${activityMarker.title}\nStarted: ${activityMarker.startDate.toLocaleDateString()}\nClick to view company details`}
            >
              {displayInfo?.isDualCompany ? (
                <DualCompanyLogo
                  assignmentCompany={displayInfo.assignmentCompany}
                  tempAgency={displayInfo.tempAgency!}
                  size="sm"
                  onAssignmentCompanyClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.location.href = `/professional/${createCompanySlug(displayInfo.assignmentCompany.name)}`;
                  }}
                  onTempAgencyClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.location.href = `/professional/${createCompanySlug(displayInfo.tempAgency!.name)}`;
                  }}
                />
              ) : (
                (() => {
                  const company = companies.find(c => c.id === activityMarker.companyId);
                  return company ? (
                    <div 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.location.href = `/professional/${createCompanySlug(company.name)}`;
                      }}
                      className="cursor-pointer hover:scale-110 transition-transform duration-200"
                      title={`View ${company.name} details`}
                    >
                      <Avatar className="w-6 h-6 flex-shrink-0">
                        <AvatarImage src={company.logoPath || undefined} />
                        <AvatarFallback className="text-xs bg-white text-primary">
                          {company.logoText}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                  ) : null;
                })()
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};